import React, { useState, useRef, useEffect } from 'react';
import { Building2, ChevronDown, Check } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../contexts/AuthContext';

const TenantSelector = ({ isMobile = false }) => {
  const { currentTenant, tenants, switchTenant, hasMultipleTenants, canSwitchTenant } = useTenant();
  const { isAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleTenantSelect = async (tenantId) => {
    if (tenantId === currentTenant?.id) {
      setIsOpen(false);
      return;
    }

    await switchTenant(tenantId);
    setIsOpen(false);
  };

  // Don't show selector if user has only one tenant or is not authenticated
  if (!hasMultipleTenants || !canSwitchTenant) {
    return null;
  }

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="btn btn-secondary btn-sm"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.5rem 0.75rem',
          minWidth: '44px',
          minHeight: '44px',
          justifyContent: 'center',
          cursor: 'pointer'
        }}
        aria-label="Select tenant"
      >
        <Building2 size={16} />
        {!isMobile && (
          <span style={{ 
            fontSize: '0.85rem',
            maxWidth: '150px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {currentTenant?.name || 'Tenant'}
          </span>
        )}
        <ChevronDown size={14} style={{ 
          transition: 'transform 0.2s',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)'
        }} />
      </button>

      {isOpen && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: '0.5rem',
          backgroundColor: 'var(--bg-primary)',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          minWidth: window.innerWidth > 480 ? '250px' : '200px',
          maxHeight: '300px',
          overflowY: 'auto',
          zIndex: 1000
        }}>
          <div style={{
            padding: '0.75rem 1rem',
            borderBottom: '1px solid #dee2e6',
            fontWeight: '600',
            fontSize: '0.9rem'
          }}>
            Избери Tenant
          </div>

          {tenants.length === 0 ? (
            <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.85rem' }}>
              Нема tenants
            </div>
          ) : (
            <div>
              {tenants.map((tenant) => (
                <button
                  key={tenant.id}
                  onClick={() => handleTenantSelect(tenant.id)}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: 'none',
                    backgroundColor: tenant.id === currentTenant?.id ? '#f8f9fa' : 'white',
                    textAlign: 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.5rem',
                    fontSize: '0.85rem',
                    transition: 'background-color 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    if (tenant.id !== currentTenant?.id) {
                      e.target.style.backgroundColor = '#f8f9fa';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (tenant.id !== currentTenant?.id) {
                      e.target.style.backgroundColor = 'white';
                    }
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: tenant.id === currentTenant?.id ? '600' : '500' }}>
                      {tenant.name}
                    </div>
                    {tenant.slug && (
                      <div style={{ 
                        fontSize: '0.75rem', 
                        color: 'var(--text-muted)',
                        marginTop: '0.25rem'
                      }}>
                        {tenant.slug}
                      </div>
                    )}
                  </div>
                  {tenant.id === currentTenant?.id && (
                    <Check size={16} style={{ color: '#28a745', flexShrink: 0 }} />
                  )}
                </button>
              ))}
            </div>
          )}

          {isAdmin && (
            <>
              <div style={{
                borderTop: '1px solid #dee2e6',
                marginTop: '0.5rem',
                paddingTop: '0.5rem'
              }}>
                <button
                  onClick={() => {
                    setIsOpen(false);
                    window.location.href = '/tenants';
                  }}
                  style={{
                    width: '100%',
                    padding: '0.5rem 1rem',
                    border: 'none',
                    backgroundColor: 'transparent',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '0.85rem',
                    color: '#059669',
                    fontWeight: '500'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  Управување со Tenants →
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default TenantSelector;

